import type { Component, JSX } from 'solid-js'
import { For, Show } from 'solid-js'
import { Trans } from '@mbarzda/solid-i18next'
import { Icon } from '@iconify-icon/solid'
import { useNumberFormatter } from '../../../shared/helpers/currency-formatter'

interface MetricBreakdownItem {
  label: string
  value: number
}

interface MetricCardProps {
  title?: string
  titleKey?: string
  value: number | string
  showSign?: boolean
  dotColor?: string
  showLineIndicator?: boolean
  format?: 'currency' | 'number' | 'percentage'
  breakdown?: MetricBreakdownItem[]
}

const MetricCard: Component<MetricCardProps> = (props) => {
  const {
    currencyFormatter,
    currencyWithSignFormatter,
    numberFormatter,
    numberWithSignFormatter,
    percentageFormatter,
    percentageWithSignFormatter
  } = useNumberFormatter()

  const hasBreakdown = () => (props.breakdown?.length ?? 0) > 0

  const formatValue = (): string => {
    if (typeof props.value === 'string') return props.value

    const numValue = props.value as number

    switch (props.format) {
      case 'currency':
        return props.showSign ? currencyWithSignFormatter(numValue) : currencyFormatter(numValue, 0)
      case 'percentage':
        return props.showSign ? percentageWithSignFormatter(numValue) ?? '' : percentageFormatter(numValue) ?? ''
      case 'number':
      default:
        return props.showSign ? numberWithSignFormatter(numValue) ?? '' : numberFormatter(numValue) ?? ''
    }
  }

  const renderTitle = (): JSX.Element => {
    if (props.titleKey) {
      return <Trans key={props.titleKey} />
    }
    return <>{props.title}</>
  }

  return (
    <div class="relative group flex-1 self-stretch">
      <div
        class="rounded-lg border border-gray-200 bg-white p-4 shadow-sm h-full"
        classList={{ 'cursor-pointer': hasBreakdown() }}
      >
        <div class="flex items-start justify-between">
          <div class="flex-1 flex flex-col items-center">
            <p class="text-xl font-bold text-gray-900 mb-1">{formatValue()}</p>
            <div class="flex items-center gap-2">
              <Show when={props.showLineIndicator}>
                <span class="flex items-center flex-shrink-0">
                  <span class="w-1.5 h-0.5 bg-gray-900" />
                  <span class="w-1.5 h-1.5 rounded-full bg-gray-900" />
                  <span class="w-1.5 h-0.5 bg-gray-900" />
                </span>
              </Show>
              <Show when={props.dotColor}>
                <span class="w-2 h-2 rounded-full flex-shrink-0" style={{ 'background-color': props.dotColor }} />
              </Show>
              <p class="text-xs font-semibold text-gray-500">{renderTitle()}</p>
              <Show when={hasBreakdown()}>
                <span class="text-gray-400 transition-transform duration-200 group-hover:rotate-180">
                  <Icon icon="heroicons:chevron-down-20-solid" class="text-[0.75rem]" />
                </span>
              </Show>
            </div>
          </div>
        </div>
      </div>
      <Show when={hasBreakdown()}>
        <div class="absolute left-0 right-0 top-full pt-2 z-30 opacity-0 -translate-y-1 pointer-events-none transition-all duration-200 ease-in-out group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto">
          <div class="bg-white rounded-lg shadow-md border border-gray-200">
            <div class="flex items-stretch px-2 py-2.5">
              <For each={props.breakdown}>
                {(item, index) => (
                  <>
                    <Show when={index() > 0}>
                      <div class="w-px bg-gray-100 mx-2" />
                    </Show>
                    <div class="flex-1 flex flex-col items-center justify-center text-center gap-0.5 px-2">
                      <span class="text-sm font-bold text-gray-900">{numberFormatter(item.value) ?? '0'}</span>
                      <span class="text-[10px] font-medium text-gray-500 leading-tight">{item.label}</span>
                    </div>
                  </>
                )}
              </For>
            </div>
          </div>
        </div>
      </Show>
    </div>
  )
}

export default MetricCard
